import { DataSource, TableColumn } from "components/molecules/Table";
import { assembleBuildingAddress, getDisplayInfo } from "utils/formatting";

export type BuildingsTableData = BuildingModelsTableDatum & DataSource;

export const buildTableData = (
    buildings: BuildingModelsTableDatum[],
    columns: (keyof BuildingModelsTableDatum | string)[]
): {
    columns: TableColumn[];
    dataSource: BuildingsTableData[];
} => {
    const dataSource = buildings.map((building: BuildingModelsTableDatum) => {
        const row: Partial<BuildingsTableData> = {
            key: building.buildingModelUid,
            buildingModelUid: building.buildingModelUid,
        };
        columns.forEach((col) => {
            if (col === "streetAddress") {
                row.streetAddress = assembleBuildingAddress(building.location);
            } else if (col === "buildingArchetype") {
                const type = building.buildingArchetype;
                const { formatFunction } = getDisplayInfo("buildingArchetype");
                row[col] = cellValueIsValid(type) ? formatFunction(type) : "";
            } else if (["buildingName", "propertyName"].includes(col)) {
                const { formatFunction } = getDisplayInfo(col);
                const name = building[col as keyof BuildingModelsTableDatum];
                row[col] = cellValueIsValid(name) ? formatFunction(name) : "";
            } else row[col] = building[col as keyof BuildingModelsTableDatum];
        });
        if (!columns.includes("tags")) row.tags = building.tags; // include tags in table data even if not visible in columns
        return row as BuildingsTableData;
    });

    return {
        columns: columns.map((column) => {
            const { formatFunction, humanReadable, unit, tooltip } =
                dataSource[0] && typeof dataSource[0][column] === "number"
                    ? getDisplayInfo(column, dataSource.map((d) => d[column]) as number[])
                    : getDisplayInfo(column);

            return {
                title: `${humanReadable} ${unit && `(${unit})`}`,
                dataIndex: column,
                key: column,
                render: (value: any) => {
                    let formattedValue = value;
                    if (column !== "buildingArchetype" && formatFunction !== undefined) {
                        formattedValue = formatFunction(value);
                    }
                    return <span>{formattedValue}</span>;
                },
                tooltip,
                customSort: column === "dataCoverage" ? sortDataCoverage : undefined,
            };
        }),
        dataSource,
    };
};

const cellValueIsValid = (value: any) => value !== null && value !== undefined;

const sortDataCoverage = (order: "asc" | "desc") => (a: BuildingModelsTableDatum, b: BuildingModelsTableDatum) => {
    const dataCoveragePriorities: {
        [key in DataCoverageEnum]: number;
    } = {
        equipment_and_utility: 1,
        equipment_and_utility_warning: 2,
        equipment: 3,
        equipment_warning: 4,
        utility: 5,
        utility_warning: 6,
        no_equipment_and_no_utility: 7,
        processing: 8,
        error: 9,
    };

    const aPriority = dataCoveragePriorities[a.dataCoverage.dataCoverage];
    const bPriority = dataCoveragePriorities[b.dataCoverage.dataCoverage];

    const result = aPriority - bPriority;
    return order === "desc" ? -result : result;
};

export const buildBuildingTableColumns = (showTags: boolean) => {
    const columns = [
        "buildingName",
        "streetAddress",
        "buildingArchetype",
        "energyType",
        "dataCoverage",
        "propertyName",
        "lastUpdated",
        "fundName",
        "grossFloorArea",
        "carbonEmissionReductionPotential",
        "averageMarginalAbatementCost",
        "netPresentValueIntensity",
        "annualUtilityCostIntensityCurrent",
        "annualCarbonPolicyCostIntensityCurrent",
        "annualCarbonEmissionIntensityCurrent",
        "annualEnergyUseIntensityCurrent",
    ];
    if (showTags) columns.splice(5, 0, "tags");
    return columns;
};
